import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Paper,
  Stack,
  Switch,
  FormControlLabel,
  Slider,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Assessment as AssessmentIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material'
import { assessmentInstrumentsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

// Status indicator styles
const activeStatusStyles = {
  active: { color: '#16a34a', label: 'Active' },
  inactive: { color: '#71717a', label: 'Inactive' },
}

function ActiveStatusIndicator({ isActive }) {
  const style = isActive ? activeStatusStyles.active : activeStatusStyles.inactive
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

// Severity indicator styles
const severityStyles = {
  minimal: { color: '#16a34a' },
  mild: { color: '#0284c7' },
  moderate: { color: '#ca8a04' },
  moderately_severe: { color: '#ea580c' },
  severe: { color: '#dc2626' },
  critical: { color: '#dc2626' },
}

function SeverityIndicator({ severity }) {
  const style = severityStyles[severity] || { color: '#71717a' }
  const label = severity?.replace(/_/g, ' ') || severity
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem', textTransform: 'capitalize' }}>
        {label}
      </Typography>
    </Box>
  )
}

function AssessmentConfig() {
  const { hasPermission } = useAuth()
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Instruments state
  const [instruments, setInstruments] = useState([])
  const [instrumentDialog, setInstrumentDialog] = useState(false)
  const [selectedInstrument, setSelectedInstrument] = useState(null)

  // Test dialog
  const [testDialog, setTestDialog] = useState(false)
  const [testInstrument, setTestInstrument] = useState(null)
  const [testResponses, setTestResponses] = useState({})
  const [testResult, setTestResult] = useState(null)

  const canEdit = hasPermission('assessment_instruments', 'update')
  const canDelete = hasPermission('assessment_instruments', 'delete')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const instrumentsData = await assessmentInstrumentsAPI.getAll()
      setInstruments(instrumentsData)
    } catch (err) {
      console.error('Error fetching data:', err)
      setError(err.response?.data?.error || 'Failed to load assessment configuration')
    } finally {
      setLoading(false)
    }
  }

  // Instrument handlers
  const handleOpenInstrumentDialog = (instrument = null) => {
    setSelectedInstrument(instrument)
    setInstrumentDialog(true)
  }

  const handleCloseInstrumentDialog = () => {
    setInstrumentDialog(false)
    setSelectedInstrument(null)
  }

  const handleDeleteInstrument = async (id) => {
    if (!window.confirm('Are you sure you want to delete this instrument?')) return
    try {
      await assessmentInstrumentsAPI.delete(id)
      fetchData()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete instrument')
    }
  }

  // Test handlers
  const handleOpenTestDialog = (instrument) => {
    setTestInstrument(instrument)
    setTestResponses({})
    setTestResult(null)
    setTestDialog(true)
  }

  const handleCloseTestDialog = () => {
    setTestDialog(false)
    setTestInstrument(null)
    setTestResponses({})
    setTestResult(null)
  }

  const handleTestScore = async () => {
    try {
      const result = await assessmentInstrumentsAPI.calculateScore(
        testInstrument.code,
        testResponses
      )
      setTestResult(result)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate score')
    }
  }


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Assessment Configuration</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage validated assessment instruments.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Instrument Library" icon={<AssessmentIcon />} iconPosition="start" />
      </Tabs>

      {/* Instrument Library Tab */}
      <TabPanel value={tab} index={0}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenInstrumentDialog()}
            >
              Add Instrument
            </Button>
          )}
        </Box>

        {instruments.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary" align="center" py={4}>
              No assessment instruments configured
            </Typography>
          </Paper>
        ) : (
          instruments.map((instrument) => (
            <Accordion key={instrument.id} defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Typography variant="body2" fontFamily="monospace" sx={{ color: 'primary.main', fontWeight: 500 }}>
                    {instrument.code}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                    {instrument.name}
                  </Typography>
                  <ActiveStatusIndicator isActive={instrument.is_active} />
                  <Typography variant="caption" color="text.secondary">
                    {instrument.questions?.length || 0} questions
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {instrument.description}
                  </Typography>
                  <Stack direction="row" spacing={2} mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      Type: <Typography component="span" variant="caption" color="text.primary">{instrument.instrument_type}</Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mode: <Typography component="span" variant="caption" color="text.primary">{instrument.administration_mode}</Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Duration: <Typography component="span" variant="caption" color="text.primary">~{instrument.estimated_duration_minutes} min</Typography>
                    </Typography>
                  </Stack>
                </Box>

                {/* Clinical Thresholds */}
                {instrument.clinical_thresholds && Object.keys(instrument.clinical_thresholds).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Clinical Thresholds</Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {Object.entries(instrument.clinical_thresholds).map(([level, range]) => (
                        <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SeverityIndicator severity={level} />
                          <Typography variant="caption" color="text.secondary">
                            ({range.min}-{range.max})
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Questions Preview */}
                {instrument.questions && instrument.questions.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Questions</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width={50}>#</TableCell>
                            <TableCell>Question</TableCell>
                            <TableCell width={100}>Critical</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {instrument.questions.slice(0, 5).map((q, i) => (
                            <TableRow key={q.id || i}>
                              <TableCell>{q.id || i + 1}</TableCell>
                              <TableCell>{q.text}</TableCell>
                              <TableCell>
                                {q.is_critical && (
                                  <Typography variant="body2" color="error.main" fontSize="0.8125rem">
                                    Critical
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                          {instrument.questions.length > 5 && (
                            <TableRow>
                              <TableCell colSpan={3}>
                                <Typography variant="caption" color="text.secondary">
                                  ... and {instrument.questions.length - 5} more questions
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Actions */}
                <Stack direction="row" spacing={1} mt={2}>
                  <Button
                    size="small"
                    startIcon={<TestIcon />}
                    onClick={() => handleOpenTestDialog(instrument)}
                  >
                    Test Scoring
                  </Button>
                  {canEdit && (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenInstrumentDialog(instrument)}
                    >
                      Edit
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteInstrument(instrument.id)}
                    >
                      Delete
                    </Button>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </TabPanel>

      {/* Test Scoring Dialog */}
      <Dialog open={testDialog} onClose={handleCloseTestDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Test Scoring: {testInstrument?.name}
        </DialogTitle>
        <DialogContent dividers>
          {testInstrument?.questions?.map((q) => (
            <Box key={q.id} mb={3}>
              <Typography variant="body2" gutterBottom>
                {q.id}. {q.text}
                {q.is_critical && (
                  <Typography component="span" variant="body2" color="error.main" sx={{ ml: 1 }}>
                    (Critical)
                  </Typography>
                )}
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={testResponses[q.id] ?? ''}
                  onChange={(e) => setTestResponses({
                    ...testResponses,
                    [q.id]: e.target.value
                  })}
                >
                  <MenuItem value="">Select response</MenuItem>
                  {q.responses?.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.value} - {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))}

          {testResult && (
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>Results</Typography>
              <Stack direction="row" spacing={3} mb={2} alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">Score</Typography>
                  <Typography variant="body1" fontWeight={500} color="primary.main">
                    {testResult.score} / {testResult.max_score}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Severity</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <SeverityIndicator severity={testResult.severity} />
                  </Box>
                </Box>
              </Stack>

              {testResult.critical_flags?.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Critical Items Flagged:</Typography>
                  {testResult.critical_flags.map((flag) => (
                    <Typography key={flag.question_id} variant="body2">
                      Q{flag.question_id}: {flag.question_text} (Response: {flag.response_value})
                    </Typography>
                  ))}
                </Alert>
              )}

            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestDialog}>Close</Button>
          <Button
            variant="contained"
            onClick={handleTestScore}
            disabled={Object.keys(testResponses).length === 0}
          >
            Calculate Score
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instrument Edit Dialog (simplified - would need full editor in production) */}
      <Dialog open={instrumentDialog} onClose={handleCloseInstrumentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedInstrument ? 'Edit Instrument' : 'Add Instrument'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            Full instrument editor coming soon. For now, instruments can be managed via the API or database directly.
          </Alert>
          {selectedInstrument && (
            <Box mt={2}>
              <Typography variant="body2">
                <strong>Code:</strong> {selectedInstrument.code}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {selectedInstrument.name}
              </Typography>
              <Typography variant="body2">
                <strong>Questions:</strong> {selectedInstrument.questions?.length || 0}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInstrumentDialog}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}

export default AssessmentConfig
